import { FlyFrame, Point, burstOffset, iconFrames, volleyTimings } from "./flyMath";
import "./rewardFly.css";

/**
 * The reward flight's sprites: one pool for the whole app (a grant never creates DOM after the first), each sprite a
 * single element animated by the Web Animations API on transform and opacity only, so the compositor plays it without
 * layout or paint (60 fps on the iPhone). `will-change` is set only while a sprite flies. The frames come from
 * flyMath.ts; this file only plays them.
 */

/** Sprites in the pool: two full volleys of BURST.MAX plus a few unique items fly at once; more simply fly fewer. */
const POOL_SIZE = 24;
/** Alternate icons bow to alternate sides. */
const SIDES: readonly (1 | -1)[] = [1, -1];

interface Sprite { readonly el: HTMLDivElement; busy: boolean }
let pool: Sprite[] | null = null;

function sprites(): Sprite[] {
  if (pool) return pool;
  const layer = document.createElement("div");
  layer.className = "rflylayer";
  layer.setAttribute("aria-hidden", "true");
  document.body.append(layer);
  pool = Array.from({ length: POOL_SIZE }, () => {
    const el = document.createElement("div");
    el.className = "rfly";
    layer.append(el);
    return { el, busy: false };
  });
  return pool;
}

/** One kind's volley: where it was earned, where it lives, the picture of each icon, and when it starts. */
export interface Volley {
  readonly origin: Point;
  readonly target: Point;
  /** One URL per icon (a counted gain repeats one picture; gear shows each piece). */
  readonly icons: readonly string[];
  readonly sizePx: number;
  /** ms after now that the volley's first icon pops. */
  readonly delayMs: number;
  /** Each icon landing: its index, and whether it is the volley's last to land. */
  onLand(i: number, last: boolean): void;
}

function keyframes(frames: readonly FlyFrame[], half: number): Keyframe[] {
  return frames.map((f) => ({ offset: f.offset, opacity: f.opacity, transform: `translate3d(${f.x - half}px,${f.y - half}px,0) scale(${f.scale})` }));
}

/** Plays a volley with the free sprites (all of it, or as much as the pool has). Returns how many icons flew. */
export function playVolley(v: Volley): number {
  const free = sprites().filter((s) => !s.busy).slice(0, v.icons.length);
  const timings = volleyTimings(free.length), half = v.sizePx / 2;
  let landed = 0;
  free.forEach((s, i) => {
    const t = timings[i], off = burstOffset(i, free.length);
    const ring = { x: v.origin.x + off.x, y: v.origin.y + off.y };
    s.busy = true;
    const st = s.el.style;
    st.backgroundImage = `url("${v.icons[i]}")`;
    st.width = st.height = `${v.sizePx}px`;
    st.willChange = "transform, opacity";
    s.el.classList.add("on");
    const anim = s.el.animate(keyframes(iconFrames(v.origin, ring, v.target, SIDES[i % SIDES.length], t), half),
      { duration: t.land - t.start, delay: v.delayMs + t.start, fill: "both", easing: "linear" });
    anim.onfinish = () => {
      anim.cancel();
      s.el.classList.remove("on");
      st.willChange = "";
      s.busy = false;
      landed++;
      v.onLand(i, landed === free.length);
    };
  });
  return free.length;
}
